<template>
  <div>
    <div v-if="(portals || sites) && selected">
      <div
        class="select-wrapper"
        :class="{
          'select-wrapper-blue': lodash.includes(
            lodash.map(portals, (p) => p.portal_id),
            selectedInternal.portal_id,
          ),
          'select-wrapper-pink': lodash.includes(
            lodash.map(sites, (s) => s.site_id),
            selectedInternal.site_id,
          ),
        }">
        <div class="header-text">
          <div class="name">{{ selectedName }}</div>
          <span v-if="showCaret" class="caret">
            <font-awesome-icon
              :icon="faIcons.faCaretDown"
              style="opacity: 0.5" />
          </span>
        </div>
        <div class="input-group">
          <select
            id="siteselectmenu"
            aria-label="Administration Site List"
            class="form-select styled-select"
            style="width: 100%"
            v-model="selectedInternal">
            <optgroup label="—— Sites" v-if="sites && sites.length > 0">
              <template v-for="(item, idx) in fullList">
                <template v-if="idx < sites.length">
                  <option :id="item.site_id" :value="item" :key="item.site_id">
                    {{ item.name }}
                  </option>
                </template>
              </template>
            </optgroup>
            <optgroup label="—— Portals" v-if="portals && portals.length > 0">
              <template v-for="(item, idx) in fullList">
                <template v-if="idx >= sites.length">
                  <option
                    :id="item.portal_id"
                    :value="item"
                    :key="item.portal_id">
                    {{ item.name }}
                  </option>
                </template>
              </template>
            </optgroup>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  name: 'menu-selector',

  components: {},

  computed: {
    // fullList is the combined list of sites and portals, with sites
    // being given first.
    fullList() {
      if (!this.sites) return [];
      if (!this.portals) return [];

      return this.sites.concat(this.portals);
    },

    selectedName() {
      try {
        if (this.selected.site_id) {
          return _.find(this.fullList, { site_id: this.selected.site_id }).name;
        }
        return _.find(this.fullList, { portal_id: this.selected.portal_id })
          .name;
      } catch (err) {
        return ''; // So we don't throw errors when we're deleting a site.
      }
    },

    showCaret() {
      return this.fullList.length > 1;
    },
  },

  created() {
    // Handle default value injection to parent here.
    this.selectedInternal = this.selected;
    if (!this.selectedInternal && this.sites.length >= 1) {
      this.selectedInternal = this.sites[0];
    }
    if (
      !this.selectedInternal &&
      this.sites.length === 0 &&
      this.portals.length >= 1
    ) {
      this.selectedInternal = this.portals[0];
    }
  },

  data() {
    return {
      selectedInternal: null,
    };
  },

  emits: ['emitSelected'],

  methods: {
    // emitSelected emits a portal/site selection to the parent.
    emitSelected() {
      this.$emit('emitSelected', this.selectedInternal);
    },
  },

  mounted() {},

  props: {
    // Arrays
    portals: {
      required: true,
    },
    sites: {
      required: true,
    },

    // Objects
    selected: {
      required: true,
    },

    // Strings
    selectedIdUpstream: {
      type: String,
    },
  },

  watch: {
    fullList: {
      handler: function handle() {
        // Handle default value injection to parent here.
        if (!this.selectedInternal && this.sites.length >= 1) {
          this.selectedInternal = this.sites[0];
        }
        if (
          !this.selectedInternal &&
          this.sites.length === 0 &&
          this.portals.length >= 1
        ) {
          this.selectedInternal = this.portals[0];
        }
      },
    },

    selected: {
      handler: function handle(val) {
        const valClone = _.cloneDeep(val);
        if (valClone) {
          valClone.fromUpstream = true;
          this.selectedInternal = val;
        }
      },
      deep: true,
    },

    // On update of selectedSite, emit the new portal to the parent. Only
    // do this if we're not transitioning from null --> defaultPortal during
    // the initialization of the component.
    selectedInternal: {
      handler: function handle(val) {
        if (val.fromUpstream) return;
        this.emitSelected();
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~assets/css/colors';

.fade-leave-active {
  transition: opacity 0.15s;
}

.fade-enter-active {
  transition: opacity 0.15s;
}

.fade-enter {
  opacity: 0;
}

.fade-leave-to {
  opacity: 0;
}

.select-wrapper {
  margin-right: 0;
  padding-right: 1rem;
  padding-top: 8px;
  padding-bottom: 8px;
  position: relative;
  padding-left: 8px;
}

.select-wrapper-blue {
  border-left: 6px solid var(--secondary-color, $secondarycolor);
}

.select-wrapper-pink {
  border-left: 6px solid var(--accent-color, $accentcolor);
}

.select-wrapper:hover {
  background-color: var(--primary-color-lighten-5, $primarycolorlighten5);
  color: #fff;
  text-decoration: none;
}

.header-text {
  color: #fff;
  font-size: 1.7rem;
  margin-bottom: 0;
  width: 100%;
  .name {
    max-width: 90%;
  }
  .caret {
    position: absolute;
    right: 1rem;
    top: 1rem;
  }
}

.styled-select {
  cursor: pointer;
  left: 0;
  top: 0;
  position: absolute;
  opacity: 0;
  background: var(--primary-color, $primarycolor);
  font-size: 18px;
  color: #fff;
  border: 0px solid #ccc;
  // height: 29px;
  // overflow: hidden;
  // width: 240px;
  height: 100%;
  word-wrap: break-word; /* IE 5.5-7 */
  white-space: -moz-pre-wrap; /* Firefox 1.0-2.0 */
  white-space: pre-wrap; /* Modern browsers */
}

// Fix for MacOSX Firefox bug.
@supports (-moz-osx-font-smoothing: auto) {
  optgroup {
    background: #fff;
    color: #000;
    font-weight: bold;
  }

  .styled-select {
    cursor: pointer;
    left: 0;
    top: 0;
    position: absolute;
    opacity: 0;
    background: #fff;
    font-size: 18px;
    color: #000;
    border: 0px solid #ccc;
    // height: 29px;
    // overflow: hidden;
    // width: 240px;
    height: 100%;
    word-wrap: break-word; /* IE 5.5-7 */
    white-space: -moz-pre-wrap; /* Firefox 1.0-2.0 */
    white-space: pre-wrap; /* Modern browsers */
  }
}

.el-dropdown-menu {
  background: var(--primary-color, $primarycolor);
  color: var(--grey-color, $greycolor);
}

.el-dropdown {
  .el-icon--right {
    opacity: 0;
  }
}

.el-dropdown:hover {
  .el-icon--right {
    opacity: 1;
  }
}

optgroup {
  font-size: 14px;
}
</style>
